import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, KeyRound, Store, MessagesSquare, Bot, Code2, Gauge, AlertTriangle, Server, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CategoryBadge, StatusPill, EmptyState, credState } from "@/components/Common";

const Stat = ({ icon: Icon, label, value, sub, c, testid }) => (
  <div className="glass p-5 fade-up" data-testid={testid}>
    <div className="flex items-center gap-4">
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${c}`}><Icon className="h-6 w-6 text-white" /></div>
      <div>
        <div className="text-2xl font-bold text-white font-display" data-testid={`${testid}-value`}>{value}</div>
        <div className="text-sm text-slate-300">{label}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </div>
    </div>
  </div>
);

const QUICK = [
  ["/app/catalog", Store, "API Marketplace", "Provider entdecken"],
  ["/app/providers", KeyRound, "Provider & Keys", "Keys verwalten"],
  ["/app/playground", MessagesSquare, "AI Playground", "Modelle testen"],
  ["/app/assistants", Bot, "Assistenten", "Vorlagen nutzen"],
  ["/app/api-access", Code2, "API-Zugang", "Hub-Keys"],
  ["/app/providers", Server, "Ollama & vLLM", "Lokale Modelle"],
];

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, []);
  if (err) return <div className="text-red-300" data-testid="dashboard-error">{err}</div>;
  if (!d) return <div className="text-slate-400">Lade Dashboard…</div>;
  const okCount = d.providers.filter((p) => p.last_test?.ok).length;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <section className="glass relative overflow-hidden p-8 sm:p-10 fade-up">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-violet-600/25 blur-3xl" />
        <div className="absolute right-40 -bottom-24 h-60 w-60 rounded-full bg-blue-600/20 blur-3xl" />
        <h1 className="relative text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">Willkommen, {user.name}</h1>
        <p className="relative mt-3 text-slate-300">Entdecken. Testen. Nutzen. Kostenlose und lokale Routen haben Vorrang.</p>
        {d.providers.length === 0 && (
          <Link to="/app/catalog" data-testid="dashboard-setup-first-provider" className="btn-grad relative mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm">
            Ersten Provider einrichten <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </section>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat testid="stat-today" icon={Gauge} c="from-blue-500 to-blue-700" label="Anfragen heute" value={d.limit == null ? d.today : `${d.today} / ${d.limit}`} sub={d.limit == null ? "Admin – kein Tageslimit" : "Tageslimit, Reset 00:00 UTC"} />
        <Stat testid="stat-providers" icon={KeyRound} c="from-violet-500 to-fuchsia-600" label="Provider eingerichtet" value={`${d.providers.length} / ${d.catalog_count}`} sub={`${okCount} erfolgreich getestet`} />
        <Stat testid="stat-week" icon={Activity} c="from-emerald-500 to-teal-600" label="Anfragen (7 Tage)" value={d.week.requests} sub={`Tokens: ${d.week.tokens_in + d.week.tokens_out || "–"}`} />
        <Stat testid="stat-errors" icon={AlertTriangle} c="from-amber-500 to-orange-600" label="Fehler (7 Tage)" value={d.week.errors} sub={d.week.requests ? `${Math.round((d.week.errors / d.week.requests) * 100)} % Fehlerquote` : "Noch keine Anfragen"} />
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <section className="glass p-6">
          <h2 className="mb-4 text-lg font-semibold text-white">Schnell starten</h2>
          <div className="grid grid-cols-2 gap-3">
            {QUICK.map(([to, I, t, s]) => (
              <Link key={t} to={to} data-testid={`quick-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-xl border border-white/5 bg-white/[0.02] p-4 transition-colors hover:border-blue-500/40 hover:bg-blue-500/5">
                <I className="mb-3 h-6 w-6 text-blue-400" /><div className="text-sm font-semibold text-white">{t}</div><div className="text-xs text-slate-500">{s}</div>
              </Link>
            ))}
          </div>
        </section>
        <section className="glass p-6">
          <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-semibold text-white">Deine Provider</h2><Link to="/app/providers" className="text-sm text-blue-400">Verwalten →</Link></div>
          {d.providers.length === 0 ? (
            <EmptyState testid="dashboard-providers-empty" icon={KeyRound} title="Noch kein Provider eingerichtet" text="Wähle im Katalog einen kostenlosen Provider wie Groq oder richte Ollama lokal ein." />
          ) : (
            <div className="space-y-2" data-testid="dashboard-provider-list">
              {d.providers.map((p) => (
                <div key={p.provider_id} className="flex items-center justify-between rounded-lg border border-white/5 px-4 py-3">
                  <div><div className="text-sm font-semibold text-white">{p.name}</div><CategoryBadge category={p.cost_status} /></div>
                  <StatusPill state={credState(p)} />
                </div>
              ))}
            </div>
          )}
        </section>
        <section className="glass p-6">
          <h2 className="mb-4 text-lg font-semibold text-white">Nutzung (7 Tage)</h2>
          {d.week.requests === 0 ? (
            <EmptyState testid="dashboard-usage-empty" icon={Activity} title="Noch keine Anfragen" text="Sobald du den Playground oder die API nutzt, erscheinen hier echte Zahlen." />
          ) : (
            <div className="h-56" data-testid="dashboard-usage-chart">
              <ResponsiveContainer>
                <AreaChart data={d.series}>
                  <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#7b3dff" stopOpacity={0.6} /><stop offset="1" stopColor="#7b3dff" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#1c2a55" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#64748b" fontSize={11} />
                  <YAxis allowDecimals={false} stroke="#64748b" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0b1226", border: "1px solid #1c2a55", borderRadius: 8 }} />
                  <Area dataKey="requests" name="Anfragen" stroke="#8b5cf6" fill="url(#g1)" strokeWidth={2} />
                  <Area dataKey="errors" name="Fehler" stroke="#f87171" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
